import { useSpaceStore } from '@/modules/space/stores/SpaceStore'
import { PlanTier } from '@/modules/team/models/Subscription'
import { useTeamStore } from '@/modules/team/stores/TeamStore'
import { TeamRole, UserState } from '@/modules/user/models/User'
import { useUserStore } from '@/modules/user/stores/UserStore'
import { defineStore } from 'pinia'

/**
 * Every permission code the plan catalog defines (`V216__ADD_PLANS_PERMISSIONS_CATALOG.sql`, plus
 * MANAGE_CRM_INTEGRATIONS / CHAT from
 * `V220__RESTORE_LEGACY_FREE_ENTITLEMENTS_AND_SPLIT_CRM_CHAT.sql` and CREATE_SPACE_LIST /
 * CONTENT_LIBRARY_UPLOAD from `V222__ADD_SPACE_LIST_AND_CONTENT_LIBRARY_UPLOAD_PERMISSIONS.sql`).
 */
const CATALOG_PERMISSIONS = [
  'LIST_SPACES',
  'EDIT_DOCUMENT',
  'CREATE_SPACE',
  'CREATE_SPACE_LIST',
  'INVITE_MEMBER',
  'MANAGE_INTEGRATIONS',
  'MANAGE_CRM_INTEGRATIONS',
  'CHAT',
  'PDF_FINALIZE',
  'CREATE_API_KEY',
  'CONTENT_LIBRARY_UPLOAD',
  'MANAGE_BRANDING',
  'MANAGE_DOMAIN',
  'MANAGE_SMTP',
  'ANALYTICS'
]

/**
 * NS-205: app feature key → catalog permission code, where the two names differ.
 *
 * The catalog models coarser buckets than these flags, and spells several of them differently:
 * invites are INVITE_MEMBER and PDF export is PDF_FINALIZE. Querying the raw flag name against a
 * real permission list would deny those features to every team, paid ones included.
 *
 * Slack and the two CRMs are *not* one bucket: legacy sold Slack on every paid plan and Salesforce
 * and HubSpot on GROWTH only, so the CRMs map to MANAGE_CRM_INTEGRATIONS (V220) while Slack keeps
 * MANAGE_INTEGRATIONS. Collapsing all three onto MANAGE_INTEGRATIONS handed START both CRMs.
 */
const CATALOG_PERMISSION_BY_FEATURE: Record<string, string> = {
  INVITE_USER: 'INVITE_MEMBER',
  SLACK_INTEGRATION: 'MANAGE_INTEGRATIONS',
  SALESFORCE_INTEGRATION: 'MANAGE_CRM_INTEGRATIONS',
  HUBSPOT_INTEGRATION: 'MANAGE_CRM_INTEGRATIONS',
  PDF_EXPORT: 'PDF_FINALIZE',
  BRANDING: 'MANAGE_BRANDING',
  CUSTOM_DOMAIN: 'MANAGE_DOMAIN',
  CUSTOM_SMTP: 'MANAGE_SMTP',
  INVITE_MEMBER: 'INVITE_MEMBER'
}

/**
 * The catalog permission a feature needs, or null when the catalog does not model it at all
 * (content library). Nothing in the catalog can withhold an unmodelled feature and the backend soft
 * lock does not block it either, so those stay allowed — soft lock included. A feature that must be
 * withheld from a plan therefore has to be modelled: that is why CHAT is a catalog permission
 * (V220) rather than an unmodelled flag that FREE passed unconditionally.
 */
function catalogPermissionFor(featureKey: string): string | null {
  if (CATALOG_PERMISSIONS.includes(featureKey)) {
    return featureKey
  }
  return CATALOG_PERMISSION_BY_FEATURE[featureKey] ?? null
}

export const useFeatureRestrictionStore = defineStore('featureRestriction', {
  state: (): {} => ({}),
  getters: {
    subscription() {
      return useTeamStore().currentTeam?.subscription
    },
    subscriptionPlan() {
      return useTeamStore().currentTeam?.subscription?.plan
    },
    appsumoPlanTier() {
      const appsumoLicense = useTeamStore().currentTeam?.appsumoLicense
      return appsumoLicense && !appsumoLicense.isDisabled ? appsumoLicense.currentPlanTier : null
    },
    spaces() {
      return useSpaceStore().activeSpaces
    },
    internalUsers() {
      return useUserStore().users.filter(user => user.role !== TeamRole.EXTERNAL && user.state !== UserState.DISABLED)
    },
    isTrialLocked(): boolean {
      // Delegated so the soft-lock fallback (`accessState`, for when the entitlement payload is
      // missing) applies here too — this getter decides how the permission-less case fails.
      return useTeamStore().isTrialLocked
    },
    // NS-205: `subscription.permissions` is the single source of truth. On soft lock the BE swaps the
    // plan to TRIAL_EXPIRED, which still grants the keep-working permissions (space list + document
    // edit), so this must not blanket-deny — that would lock users out of their own work.
    hasPermission(): (key: string) => boolean {
      return (key: string): boolean => {
        const subscription = useTeamStore().currentTeam?.subscription
        if (!subscription) {
          return true
        }
        const permission = catalogPermissionFor(key)
        if (!permission) {
          return true
        }
        const perms = subscription.permissions
        if (perms == null) {
          // NS-203: no permission payload. If the entitlement read was attempted and failed, the
          // team's real plan is unknown — fail closed, because the alternative is showing a BASIC
          // team branding and content-library upload and 403-ing them on click. If it was never
          // attempted (the Salesforce widget loads a team without entitlements), keep the
          // pre-NS-205 permissive behaviour; only a soft-locked team stays closed there.
          if (useTeamStore().entitlementsUnavailable) {
            return false
          }
          return !this.isTrialLocked
        }
        return perms.includes(permission)
      }
    },
    isPlan:
      () =>
      (...plans: string[]): boolean => {
        const plan = useTeamStore().currentTeam?.subscription?.plan
        return plan ? plans.includes(plan) : false
      },
    allowCreateSpace(): boolean {
      if (this.appsumoPlanTier || !this.subscription) {
        return true
      }

      return this.hasPermission('CREATE_SPACE')
    },
    // V222. Creating a list is gated; reading, renaming and deleting existing lists are not, so
    // there is deliberately no `allowManageSpaceList` counterpart.
    allowCreateSpaceList(): boolean {
      if (this.appsumoPlanTier || !this.subscription) {
        return true
      }

      return this.hasPermission('CREATE_SPACE_LIST')
    },
    exceedSpaceLimit(): boolean {
      if (this.appsumoPlanTier || !this.subscription) {
        return false
      }

      return false
    },
    allowInviteUser(): boolean {
      switch (this.appsumoPlanTier) {
        case PlanTier.TIER1:
        case PlanTier.NOTCH_TIER1_V2:
          return this.internalUsers.length < 3
        case PlanTier.TIER2:
        case PlanTier.NOTCH_TIER2_V2:
          return this.internalUsers.length < 10
        case PlanTier.TIER3:
        case PlanTier.NOTCH_TIER3_V2:
          return this.internalUsers.length < 25
        case PlanTier.NOTCH_TIER4_V2:
          return true
      }

      if (!this.subscription) {
        return true
      }

      return this.hasPermission('INVITE_USER') && !this.exceedUserLimit
    },
    exceedUserLimit(): boolean {
      switch (this.appsumoPlanTier) {
        case PlanTier.NOTCH_TIER1_V2:
          return this.internalUsers.length >= 3
        case PlanTier.NOTCH_TIER2_V2:
          return this.internalUsers.length >= 10
        case PlanTier.NOTCH_TIER3_V2:
          return this.internalUsers.length >= 25
        case PlanTier.NOTCH_TIER4_V2:
          return false
        case PlanTier.TIER1:
          return this.internalUsers.length >= 3
        case PlanTier.TIER2:
          return this.internalUsers.length >= 10
        case PlanTier.TIER3:
          return this.internalUsers.length >= 25
      }

      const subscription = this.subscription
      if (!subscription) {
        return false
      }
      // Plan seat limit from the catalog (BASIC = 3). null/undefined means unlimited, which is every
      // other plan and also the state before the entitlement payload has been merged in — so a
      // missing payload never invents a cap. The BE enforces the same limit on invite.
      const maxUsers = subscription.maxUsers
      if (maxUsers == null) {
        return false
      }
      return this.internalUsers.length >= maxUsers
    },
    allowContentLibrary(): boolean {
      if (this.appsumoPlanTier || !this.subscription) {
        return true
      }
      return this.hasPermission('CONTENT_LIBRARY')
    },
    // V222. Split from `allowContentLibrary` on purpose: browsing the library and inserting what is
    // already in it stay open for a soft-locked team (existing work), only adding to it is paid.
    allowContentLibraryUpload(): boolean {
      if (this.appsumoPlanTier || !this.subscription) {
        return true
      }
      return this.hasPermission('CONTENT_LIBRARY_UPLOAD')
    },
    // Developer Center is read-only for a team whose plan does not grant CREATE_API_KEY: existing
    // keys, webhooks and logs stay visible, but create/edit/revoke are locked. The catalog models
    // the keys only, and the webhook/OAuth-app credentials ride with them — the same grouping
    // `SoftLockInterceptor` uses for `/internal/webhooks` and `/internal/oauth-apps`.
    allowDeveloperMutations(): boolean {
      if (this.appsumoPlanTier || !this.subscription) {
        return true
      }
      return this.hasPermission('CREATE_API_KEY')
    },
    allowInviteMember(): boolean {
      if (this.appsumoPlanTier || !this.subscription) {
        return true
      }
      return this.hasPermission('INVITE_MEMBER')
    },
    allowSlackIntegration(): boolean {
      if (this.appsumoPlanTier) {
        switch (this.appsumoPlanTier) {
          case PlanTier.NOTCH_TIER1_V2:
          case PlanTier.TIER1:
            return false
          case PlanTier.NOTCH_TIER2_V2:
          case PlanTier.NOTCH_TIER3_V2:
          case PlanTier.NOTCH_TIER4_V2:
          case PlanTier.TIER2:
          case PlanTier.TIER3:
            return true
        }
      }

      if (!this.subscription) {
        return true
      }

      return this.hasPermission('SLACK_INTEGRATION')
    },

    allowSalesforceIntegration(): boolean {
      if (this.appsumoPlanTier) {
        switch (this.appsumoPlanTier) {
          case PlanTier.NOTCH_TIER1_V2:
          case PlanTier.TIER1:
            return false
          case PlanTier.NOTCH_TIER2_V2:
          case PlanTier.NOTCH_TIER3_V2:
          case PlanTier.NOTCH_TIER4_V2:
          case PlanTier.TIER2:
          case PlanTier.TIER3:
            return true
        }
      }

      if (!this.subscription) {
        return true
      }

      return this.hasPermission('SALESFORCE_INTEGRATION')
    },

    allowHubspotIntegration(): boolean {
      if (this.appsumoPlanTier) {
        switch (this.appsumoPlanTier) {
          case PlanTier.NOTCH_TIER1_V2:
          case PlanTier.TIER1:
            return false
          case PlanTier.NOTCH_TIER2_V2:
          case PlanTier.NOTCH_TIER3_V2:
          case PlanTier.NOTCH_TIER4_V2:
          case PlanTier.TIER2:
          case PlanTier.TIER3:
            return true
        }
      }

      if (!this.subscription) {
        return true
      }

      return this.hasPermission('HUBSPOT_INTEGRATION')
    },
    allowSignature(): boolean {
      return true
    },
    allowAnalytics(): boolean {
      if (this.appsumoPlanTier) {
        switch (this.appsumoPlanTier) {
          case PlanTier.NOTCH_TIER1_V2:
            return false
          case PlanTier.NOTCH_TIER2_V2:
          case PlanTier.NOTCH_TIER3_V2:
          case PlanTier.NOTCH_TIER4_V2:
          case PlanTier.TIER1:
          case PlanTier.TIER2:
          case PlanTier.TIER3:
            return true
        }
      }

      if (!this.subscription) {
        return true
      }

      return this.hasPermission('ANALYTICS')
    },
    allowBranding(): boolean {
      if (this.appsumoPlanTier) {
        switch (this.appsumoPlanTier) {
          case PlanTier.NOTCH_TIER1_V2:
          case PlanTier.NOTCH_TIER2_V2:
          case PlanTier.TIER1:
          case PlanTier.TIER2:
            return false
          case PlanTier.NOTCH_TIER3_V2:
          case PlanTier.NOTCH_TIER4_V2:
          case PlanTier.TIER3:
            return true
        }
      }

      if (!this.subscription) {
        return true
      }

      return this.hasPermission('BRANDING')
    },
    allowChat(): boolean {
      if (this.appsumoPlanTier) {
        switch (this.appsumoPlanTier) {
          case PlanTier.NOTCH_TIER1_V2:
          case PlanTier.NOTCH_TIER2_V2:
            return false
          case PlanTier.NOTCH_TIER3_V2:
          case PlanTier.NOTCH_TIER4_V2:
          case PlanTier.TIER1:
          case PlanTier.TIER2:
          case PlanTier.TIER3:
            return true
        }
      }

      if (!this.subscription) {
        return true
      }

      return this.hasPermission('CHAT')
    },
    spacesCount(): number {
      return this.spaces.length
    },
    allowCustomDomains(): boolean {
      if (this.appsumoPlanTier) {
        switch (this.appsumoPlanTier) {
          case PlanTier.NOTCH_TIER1_V2:
          case PlanTier.NOTCH_TIER2_V2:
          case PlanTier.TIER1:
          case PlanTier.TIER2:
            return false
          case PlanTier.NOTCH_TIER3_V2:
          case PlanTier.NOTCH_TIER4_V2:
          case PlanTier.TIER3:
            return true
        }
      }
      if (!this.subscription) {
        return true
      }

      return this.hasPermission('CUSTOM_DOMAIN')
    },

    allowCustomSmtp(): boolean {
      if (this.appsumoPlanTier) {
        switch (this.appsumoPlanTier) {
          case PlanTier.NOTCH_TIER1_V2:
          case PlanTier.NOTCH_TIER2_V2:
          case PlanTier.TIER1:
          case PlanTier.TIER2:
            return false
          case PlanTier.NOTCH_TIER3_V2:
          case PlanTier.NOTCH_TIER4_V2:
          case PlanTier.TIER3:
            return true
        }
      }
      if (!this.subscription) {
        return true
      }

      return this.hasPermission('CUSTOM_SMTP')
    },

    allowIntegrations(): boolean {
      return this.allowSlackIntegration || this.allowSalesforceIntegration || this.allowHubspotIntegration
    },

    allowPDFExport(): boolean {
      if (this.appsumoPlanTier || !this.subscription) {
        return true
      }

      return this.hasPermission('PDF_EXPORT')
    }
  }
})

export type FeatureRestrictionStore = ReturnType<typeof useFeatureRestrictionStore>
